// FieldModal.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useFieldModalState from './hooks/useFieldModalState';

// CSS
import './FieldModal.css';

// Components
import FieldDeleteConfirm from './components/FieldDeleteConfirm';
import FieldEditorForm from './FieldEditorForm';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

/**
 * FieldModal
 *
 * Modal for adding or editing a schema form field.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onSave - Callback when the field is saved.
 * @param {string[]|null} allowedFieldTypes - Optional allowlist of field types to expose in the picker.
 * 
 * @returns {ReactNode} Rendered FieldModal component.
 */
const FieldModal = ({
    isOpen,
    setOpen,
    onSave,
    onDelete,
    isSaving = false,
    editingField = null,
    nodeTypeLabel = '',
    existingFields = [],
    baseKey,
    allowedFieldTypes = null,
    typeKey = '',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- HOOKS ----- //

    const {
        modalState,
        formState,
        checklistItemsState,
        enumOptionsState,
    } = useFieldModalState({
        isOpen,
        onSave,
        editingField,
        nodeTypeLabel,
        existingFields,
        allowedFieldTypes,
        typeKey,
    });

    // ----- RENDER ----- //

    return (
        <>
            <Modal
              title={modalState.modalTitle}
              isOpen={isOpen}
              width={isMobile ? "100%" : 480}
              height="auto"
              onClose={() => setOpen(false)}
            >
                <ModalForm>
                    <FieldEditorForm
                      checklistItemsState={checklistItemsState}
                      enumOptionsState={enumOptionsState}
                      formState={formState}
                      isEditing={modalState.isEditing}
                      theme={theme}
                    />
                </ModalForm>

                {/* ACTIONS */}
                <ModalButtons>
                    <ModalButtonSection align="START">
                        {modalState.isEditing && typeof onDelete === 'function' ? (
                            <Button
                              iconKey="delete"
                              label="Delete"
                              enabled={modalState.canDelete && !isSaving}
                              onClick={() => modalState.setConfirmDeleteOpen(true)}
                            />
                        ) : null}
                    </ModalButtonSection>

                    <ModalButtonSection align="END">
                        <Button
                          iconKey="close"
                          label="Cancel"
                          enabled={!isSaving}
                          onClick={() => setOpen(false)}
                        />

                        <Button
                          iconKey="check"
                          label={isSaving ? 'Saving...' : 'Save'}
                          enabled={!isSaving}
                          onClick={formState.handleSave}
                        />
                    </ModalButtonSection>
                </ModalButtons>

            </Modal >

            <FieldDeleteConfirm
              isOpen={modalState.confirmDeleteOpen}
              setOpen={modalState.setConfirmDeleteOpen}
              deleteLabel={modalState.deleteLabel}
              canDelete={modalState.canDelete}
              onDelete={onDelete}
            />
        </>
    );
};

// Dev Tools
FieldModal.displayName = 'FieldModal';

export default FieldModal;
